import PropTypes from 'prop-types';
import {
  Grid,
  Typography,
  FormControl,
  FormControlLabel,
  RadioGroup,
  makeStyles,
} from '@material-ui/core';
import { Field } from 'formik';
import cx from 'classnames';
import Radio from '../../../Shared/Radio';
import HelperText from '../../../Shared/HelperText';
import { orderTypes } from '../../constants';

const useStyles = makeStyles((theme) => ({
  formGroup: {
    marginBottom: theme.spacing(4),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  groupTitle: {
    fontSize: '1rem',
    fontWeight: 500,
    color: theme.colors?.blue2 || '#122056',
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
  },
  groupContent: {
    paddingLeft: theme.spacing(2),
  },
  treatmentTypeControl: {
    width: '100%',
  },
  treatmentOption: {
    border: '2px solid #e0e0e0',
    borderRadius: 8,
    padding: theme.spacing(2),
    transition: 'all 0.2s ease',
    backgroundColor: '#fff',
  },
  treatmentOptionNotDisabled: {
    cursor: 'pointer',

    '&:hover': {
      borderColor: theme.colors?.lightBlue || '#5B65DC',
      backgroundColor: '#f8fbff',
    },
  },
  selected: {
    borderColor: theme.colors?.lightBlue || '#5B65DC',
    backgroundColor: '#f0f8ff',
    boxShadow: '0 2px 8px rgba(0, 168, 255, 0.15)',
  },
  treatmentRadio: {
    margin: 0,
    width: '100%',
    '& .MuiFormControlLabel-label': {
      width: '100%',
    },
  },
  treatmentLabel: {
    width: '100%',
  },
  treatmentTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
    color: theme.colors?.blue2 || '#122056',
  },
}));

const TreatmentTypeSelector = ({ isEditable, backendError }) => {
  const classes = useStyles();

  return (
    <div className={classes.formGroup}>
      <Typography className={classes.groupTitle}>
        Treatment Type
      </Typography>
      <div className={classes.groupContent}>
        <Field name='category' type='checkbox'>
          {({ field, form }) => (
            <FormControl
              required
              error={!!backendError}
              disabled={!isEditable}
              className={classes.treatmentTypeControl}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <div
                    className={cx(classes.treatmentOption, {
                      [classes.selected]: field.value === orderTypes.SLIT,
                      [classes.treatmentOptionNotDisabled]: isEditable
                    })}
                    onClick={isEditable ? () => form.setFieldValue(field.name, orderTypes.SLIT) : null}
                  >
                    <FormControlLabel
                      value={orderTypes.SLIT}
                      control={<Radio checked={field.value === orderTypes.SLIT} />}
                      label={
                        <div className={classes.treatmentLabel}>
                          <Typography variant="h6" className={classes.treatmentTitle}>
                            SLIT
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Sublingual Immunotherapy
                          </Typography>
                        </div>
                      }
                      className={classes.treatmentRadio}
                    />
                  </div>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <div
                    className={cx(classes.treatmentOption, {
                      [classes.selected]: field.value === orderTypes.SCIT,
                      [classes.treatmentOptionNotDisabled]: isEditable
                    })}
                    onClick={isEditable ? () => form.setFieldValue(field.name, orderTypes.SCIT) : null}
                  >
                    <FormControlLabel
                      disabled={!isEditable}
                      value={orderTypes.SCIT}
                      control={<Radio checked={field.value === orderTypes.SCIT} />}
                      label={
                        <div className={classes.treatmentLabel}>
                          <Typography variant="h6" className={classes.treatmentTitle}>
                            SCIT
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Subcutaneous Immunotherapy
                          </Typography>
                        </div>
                      }
                      className={classes.treatmentRadio}
                    />
                  </div>
                </Grid>
              </Grid>
              <RadioGroup
                row
                name={field.name}
                value={field.value}
                onChange={(e) => {
                  form.setFieldValue(field.name, e.target.value);
                }}
                style={{ display: 'none' }}
                disabled={!isEditable}
              >
                <FormControlLabel
                  value={orderTypes.SLIT}
                  control={<Radio />}
                  label=""
                  disabled={!isEditable}
                />
                <FormControlLabel
                  value={orderTypes.SCIT}
                  control={<Radio />}
                  label=""
                  disabled={!isEditable}
                />
              </RadioGroup>
              <HelperText>{backendError}</HelperText>
            </FormControl>
          )}
        </Field>
      </div>
    </div>
  );
};

TreatmentTypeSelector.propTypes = {
  isEditable: PropTypes.bool,
  backendError: PropTypes.string,
};

TreatmentTypeSelector.defaultProps = {
  isEditable: true,
  backendError: undefined,
};

export default TreatmentTypeSelector;
